import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';

const API_BASE = '/api';

const GEXDEXPage = () => {
  const [symbol, setSymbol] = useState('NIFTY');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const fetchData = async () => {
    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE}/gex-dex/${symbol}`);
      const result = await response.json();
      
      if (result.status === 'success') {
        setData(result);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [symbol]);
  
  const clusters = data?.clusters || [];
  const spotPrice = data?.spot_price || 0;
  const config = data?.config || {};
  
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #ffffff, #f5f5f5)',
          padding: '16px 20px',
          borderRadius: '16px',
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '800' }}>
              🎯 GEX/DEX Cluster Analysis
            </h1>
            {data && (
              <div style={{ marginTop: '6px', fontSize: '0.8rem', color: '#666' }}>
                Spot: ₹{spotPrice.toFixed(2)} | Step: {config.step} | Window: ±{config.cluster_window}
              </div>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '10px' }}>
            {['NIFTY', 'BANKNIFTY', 'SENSEX', 'FINNIFTY'].map(idx => (
              <button
                key={idx}
                onClick={() => setSymbol(idx)}
                style={{
                  padding: '8px 16px',
                  background: symbol === idx ? '#667eea' : '#eee',
                  color: symbol === idx ? '#fff' : '#333',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '700',
                  fontSize: '0.85rem'
                }}
              >
                {idx}
              </button>
            ))}
          </div>
        </div>
        
        {/* Loading */}
        {loading && !data && (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px', 
            color: '#fff',
            fontSize: '1.2rem'
          }}>
            ⏳ Loading clusters...
          </div>
        )}
        
        {/* Clusters Grid */}
        <div style={{ display: 'grid', gap: '16px' }}>
          {clusters.map((cluster, idx) => {
            const isCenter = idx === Math.floor(clusters.length / 2);
            
            return (
              <div key={cluster.anchor_strike} style={{
                background: '#fff',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: isCenter 
                  ? '0 8px 24px rgba(102,126,234,0.4)' 
                  : '0 4px 12px rgba(0,0,0,0.1)',
                border: isCenter ? '3px solid #667eea' : 'none'
              }}>
                {/* Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px',
                  paddingBottom: '12px',
                  borderBottom: '2px solid #eee'
                }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <h3 style={{ margin: 0, fontSize: '1.3rem', fontWeight: '800' }}>
                        Anchor: {cluster.anchor_strike}
                      </h3>
                      {isCenter && (
                        <span style={{
                          padding: '2px 8px',
                          background: '#667eea',
                          color: '#fff',
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          fontWeight: '700'
                        }}>
                          ACTIVE
                        </span>
                      )}
                    </div>
                    <p style={{ margin: '4px 0 0', color: '#666', fontSize: '0.85rem' }}>
                      Range: {cluster.lower_bound} - {cluster.upper_bound} 
                      <span style={{ marginLeft: '8px', color: '#999' }}>
                        ({cluster.strikes_count} strikes)
                      </span>
                    </p>
                  </div>
                  
                  {/* Net Gamma Card */}
                  <div style={{
                    padding: '10px 16px',
                    background: cluster.net_gamma > 0 ? '#e8f5e9' : '#ffebee',
                    borderRadius: '10px',
                    border: `2px solid ${cluster.net_gamma > 0 ? '#4CAF50' : '#f44336'}`,
                    minWidth: '140px'
                  }}>
                    <div style={{ 
                      fontSize: '0.7rem', 
                      color: '#666', 
                      marginBottom: '4px',
                      textAlign: 'center'
                    }}>
                      Net Γ Exposure
                    </div>
                    <div style={{
                      fontSize: '1.2rem',
                      fontWeight: '900',
                      color: cluster.net_gamma > 0 ? '#4CAF50' : '#f44336',
                      textAlign: 'center'
                    }}>
                      {cluster.net_gamma > 0 ? '+' : ''}{cluster.net_gamma.toLocaleString()}
                    </div>
                  </div>
                </div>
                
                {/* Metrics Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '12px'
                }}>
                  <MetricCard
                    label="Net Δ Exposure"
                    value={cluster.net_delta}
                    formatted={`${cluster.net_delta > 0 ? '+' : ''}${cluster.net_delta.toLocaleString()}`}
                    color={cluster.net_delta > 0 ? '#2196f3' : '#ff9800'}
                  />
                  
                  <MetricCard
                    label="Normalized Δ"
                    value={cluster.normalized_delta}
                    formatted={(cluster.normalized_delta * 100).toFixed(2) + '%'}
                    color="#9c27b0"
                  />
                  
                  <MetricCard
                    label="PCR"
                    value={cluster.pcr}
                    formatted={cluster.pcr.toFixed(2)}
                    color={cluster.pcr > 1 ? '#00c853' : '#f44336'}
                  />
                  
                  <MetricCard
                    label="CE OI"
                    value={cluster.total_ce_oi}
                    formatted={cluster.total_ce_oi.toLocaleString()}
                    color="#4CAF50"
                  />
                  
                  <MetricCard
                    label="PE OI"
                    value={cluster.total_pe_oi}
                    formatted={cluster.total_pe_oi.toLocaleString()}
                    color="#f44336"
                  />
                  
                  <MetricCard
                    label="CE Γ"
                    value={cluster.ce_gamma}
                    formatted={cluster.ce_gamma.toLocaleString()}
                    color="#00bcd4"
                  />
                  
                  <MetricCard
                    label="PE Γ"
                    value={cluster.pe_gamma}
                    formatted={cluster.pe_gamma.toLocaleString()}
                    color="#ff5722"
                  />
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Info Footer */}
        {data && (
          <div style={{
            marginTop: '20px',
            padding: '12px 16px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '8px',
            color: '#fff',
            fontSize: '0.75rem',
            textAlign: 'center'
          }}>
            ℹ️ Anchors auto-generated from spot price ({spotPrice.toFixed(2)}) | 
            Updates every 5 seconds | 
            Window: ±{config.cluster_window} points per cluster
          </div>
        )}
      </div>
    </div>
  );
};

const MetricCard = ({ label, value, formatted, color }) => (
  <div style={{
    padding: '12px',
    background: `${color}15`,
    borderRadius: '8px',
    border: `1px solid ${color}40`
  }}>
    <div style={{
      fontSize: '0.7rem',
      color: '#666',
      marginBottom: '4px',
      fontWeight: '600'
    }}>
      {label}
    </div>
    <div style={{
      fontSize: '1rem',
      fontWeight: '900',
      color: color
    }}>
      {formatted}
    </div>
  </div>
);

export default GEXDEXPage;